import React, { useEffect, useMemo, useState } from 'react'
import {
  FaAward,
  FaBookOpen,
  FaCalendarAlt,
  FaChartLine,
  FaClock,
  FaMedal,
  FaUniversity,
} from 'react-icons/fa'
import { BASE_URL_Upload } from '../Component/commonComponents/ApiEndpoints'
import { useSidebar } from '../Component/SidebarContext'

const REGISTRY_TITLE = 'Passing Certificate'

const formatDdMmYyyy = (value) => {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'string' && /^\d{1,2}-\d{1,2}-\d{4}$/.test(value.trim())) {
    return value.trim()
  }
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) {
    return typeof value === 'string' ? value : ''
  }
  const day = String(d.getDate()).padStart(2, '0')
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const year = d.getFullYear()
  return `${day}-${month}-${year}`
}

const safeStr = (v, fallback = '') => {
  if (v === null || v === undefined) return fallback
  const s = String(v).trim()
  if (!s || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined') return fallback
  return s
}

const parsePercentage = (v) => {
  if (v === null || v === undefined || v === '') return null
  const n = Number(String(v).replace(/%/g, '').trim())
  return Number.isFinite(n) ? n : null
}

const resolveMediaUrl = (src) => {
  const t = safeStr(src, '')
  if (!t) return ''
  if (/^https?:\/\//i.test(t)) return t
  if (t.startsWith('//')) return `https:${t}`
  const base = String(BASE_URL_Upload || '').replace(/\/$/, '')
  const path = t.startsWith('/') ? t : `/${t}`
  return base ? `${base}${path}` : t
}

const displayTitleFromProps = (certificateTitle) => {
  const t = safeStr(certificateTitle, '')
  if (!t || t === REGISTRY_TITLE) return 'Passing Certificate'
  return t
}

const getResultClass = (percentage) => {
  if (percentage === null) return ''
  if (percentage >= 75) return 'Distinction'
  if (percentage >= 60) return 'First Class'
  if (percentage >= 45) return 'Second Class'
  return 'Pass'
}

const getRemark = (certificate, percentage) => {
  const raw =
    certificate?.remark ??
    certificate?.result_remark ??
    certificate?.classification ??
    certificate?.division
  const direct = safeStr(raw, '')
  if (direct) return direct
  return getResultClass(percentage)
}

const CertificateTemplate5 = ({
  studentName = 'Student Name',
  courseName = 'Course Name',
  instituteName = 'Institute Name',
  date = '',
  certificateTitle = 'Passing Certificate',
  certificateId = '',
  logoUrl = '',
  authorityName = 'Authorized Signatory',
  student: studentProp,
  course: courseProp,
  certificate: certificateProp,
  institute: instituteProp,
  selectedStudent,
  selectedCourse,
  suppressSidebarOffset = false,
}) => {
  const certificate = certificateProp || {}
  const student = studentProp || selectedStudent || {}
  const course = courseProp || selectedCourse || {}
  const institute = instituteProp || {}

  const { isCollapsed } = useSidebar()
  const [marginLeft, setMarginLeft] = useState('0px')
  const [photoFailed, setPhotoFailed] = useState(false)
  const [fallbackPhotoFailed, setFallbackPhotoFailed] = useState(false)

  useEffect(() => {
    if (suppressSidebarOffset) return undefined
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed, suppressSidebarOffset])

  useEffect(() => {
    setPhotoFailed(false)
    setFallbackPhotoFailed(false)
  }, [student?.student_image, student?.photo, student?.image])

  const data = useMemo(() => {
    const instName =
      safeStr(institute.institute_name) ||
      safeStr(institute.name) ||
      safeStr(instituteName)

    const studName = safeStr(student.name) || safeStr(studentName)
    const father = safeStr(student.father_name)
    const mother = safeStr(student.mother_name)
    const admission = safeStr(student.admission_number)

    const crsName =
      safeStr(course.course_name) || safeStr(course.name) || safeStr(courseName)
    const durationRaw = course.course_duration ?? course.duration
    const duration = (() => {
      if (durationRaw === null || durationRaw === undefined) return ''
      const s = String(durationRaw).trim()
      if (!s) return ''
      return /month/i.test(s) ? s : `${s} months`
    })()

    const start = formatDdMmYyyy(course.start_date ?? course.startDate)
    const end = formatDdMmYyyy(course.end_date ?? course.endDate)

    const certNum =
      safeStr(certificate.certificate_number) ||
      safeStr(certificate.certificate_id) ||
      safeStr(certificate.id != null ? String(certificate.id) : '') ||
      safeStr(certificateId)

    const issueRaw =
      certificate.issue_date ?? certificate.date ?? certificate.issueDate ?? date
    const issue = formatDdMmYyyy(issueRaw) || (typeof issueRaw === 'string' ? issueRaw : '')

    const declarationRaw =
      certificate.result_declaration_date ??
      certificate.declaration_date ??
      certificate.declarationDate
    const declaration =
      formatDdMmYyyy(declarationRaw) || (typeof declarationRaw === 'string' ? declarationRaw : '')

    const pct = parsePercentage(certificate.percentage ?? certificate.percent)
    const grade = safeStr(certificate.grade)
    const remark = getRemark(certificate, pct)
    const resultClass = getResultClass(pct)

    const imgRaw =
      student.student_image ?? student.photo ?? student.image ?? student.profile_image
    const photoSrc = resolveMediaUrl(imgRaw)

    const titlePrinted = displayTitleFromProps(certificateTitle)

    return {
      instName,
      studName,
      father,
      mother,
      admission,
      crsName,
      duration,
      start,
      end,
      certNum,
      issue,
      declaration,
      pct,
      grade,
      remark,
      resultClass,
      photoSrc,
      titlePrinted,
    }
  }, [
    certificate,
    student,
    course,
    institute,
    studentName,
    courseName,
    instituteName,
    certificateId,
    certificateTitle,
    date,
  ])

  const {
    instName,
    studName,
    father,
    mother,
    admission,
    crsName,
    duration,
    start,
    end,
    certNum,
    issue,
    declaration,
    pct,
    grade,
    remark,
    resultClass,
    photoSrc,
    titlePrinted,
  } = data

  const parentsLine =
    father && mother
      ? `S/o ${father} and M/o ${mother}`
      : father
        ? `S/o ${father}`
        : mother
          ? `M/o ${mother}`
          : ''

  const coursePeriod =
    start && end ? `${start} to ${end}` : start || end ? `${start || '—'} to ${end || '—'}` : ''

  const qrData = useMemo(() => {
    const parts = [instName, certNum || 'PREVIEW', issue || formatDdMmYyyy(new Date())].filter(
      Boolean,
    )
    return parts.join('|')
  }, [instName, certNum, issue])

  const qrSrc =
    typeof window !== 'undefined'
      ? `https://api.qrserver.com/v1/create-qr-code/?size=72x72&margin=0&data=${encodeURIComponent(
          qrData,
        )}`
      : ''

  const defaultPhoto = `${process.env.PUBLIC_URL || ''}/studentprofile.jpg`
  const showPhoto = photoSrc && !photoFailed
  const logoResolved = resolveMediaUrl(logoUrl) || logoUrl
  const layoutMarginLeft = suppressSidebarOffset ? '0px' : marginLeft

  const handlePrint = () => {
    window.print()
  }

  return (
    <div
      className="certificate-template-5-page"
      style={{
        marginLeft: layoutMarginLeft,
        transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        willChange: 'margin-left',
        WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=Inter:wght@400;500;600;700&display=swap');

          .certificate-template-5-page {
            min-height: 100vh;
            background: linear-gradient(160deg, #e8eef9 0%, #f8fafc 45%, #e2e8f0 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 20px;
            box-sizing: border-box;
          }

          .certificate-template-5-wrap {
            width: min(297mm, 100%);
          }

          .certificate-template-5-actions {
            display: flex;
            justify-content: flex-end;
            margin-bottom: 12px;
          }

          .certificate-template-5-print-btn {
            border: none;
            background: linear-gradient(135deg, #1d4ed8, #1e3a8a);
            color: #fff;
            padding: 10px 18px;
            border-radius: 10px;
            font-weight: 700;
            font-family: 'Inter', sans-serif;
            font-size: 13px;
            cursor: pointer;
            box-shadow: 0 8px 18px rgba(30, 58, 138, 0.22);
          }

          .certificate-template-5-card {
            width: 100%;
            aspect-ratio: 297 / 210;
            max-height: min(90vh, 210mm);
            background: #fff;
            border: 1px solid #cbd5e1;
            box-shadow: 0 20px 44px rgba(15, 23, 42, 0.12);
            border-radius: 8px;
            position: relative;
            overflow: hidden;
          }

          .certificate-template-5-card::before {
            content: '';
            position: absolute;
            inset: 10px;
            border: 3px double #1e3a8a;
            pointer-events: none;
          }

          .certificate-template-5-card::after {
            content: '';
            position: absolute;
            inset: 18px;
            border: 1px solid #d4af37;
            pointer-events: none;
          }

          .certificate-template-5-watermark {
            position: absolute;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: 'Cormorant Garamond', serif;
            font-size: clamp(54px, 12vw, 110px);
            color: rgba(30, 58, 138, 0.06);
            letter-spacing: 0.12em;
            text-transform: uppercase;
            pointer-events: none;
            user-select: none;
          }

          .certificate-template-5-inner {
            position: relative;
            z-index: 1;
            height: 100%;
            box-sizing: border-box;
            padding: clamp(16px, 2.2vw, 24px) clamp(18px, 2.8vw, 30px);
            display: flex;
            flex-direction: column;
            min-height: 0;
          }

          .certificate-template-5-head {
            text-align: center;
            flex-shrink: 0;
          }

          .certificate-template-5-logo {
            width: 54px;
            height: 54px;
            margin: 0 auto 6px;
            border-radius: 50%;
            border: 2px solid #1e3a8a;
            object-fit: cover;
            background: #fff;
          }

          .certificate-template-5-logo-ph {
            width: 54px;
            height: 54px;
            margin: 0 auto 6px;
            border-radius: 50%;
            border: 2px dashed #94a3b8;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 9px;
            color: #94a3b8;
            font-family: 'Inter', sans-serif;
          }

          .certificate-template-5-institute {
            margin: 0;
            font-family: 'Inter', sans-serif;
            font-size: clamp(12px, 1.4vw, 14px);
            font-weight: 700;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: #1e3a8a;
          }

          .certificate-template-5-title {
            margin: 6px 0 0;
            font-family: 'Cormorant Garamond', serif;
            font-size: clamp(24px, 3vw, 32px);
            font-weight: 700;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: #0f172a;
          }

          .certificate-template-5-title-line {
            width: min(280px, 70%);
            height: 2px;
            margin: 8px auto 0;
            background: linear-gradient(90deg, transparent, #d4af37, transparent);
          }

          .certificate-template-5-main {
            margin-top: 10px;
            display: grid;
            grid-template-columns: 1fr min(130px, 24%);
            gap: 14px 16px;
            align-items: start;
            flex: 1;
            min-height: 0;
          }

          .certificate-template-5-body {
            font-family: 'Inter', sans-serif;
            font-size: clamp(11px, 1.15vw, 13px);
            color: #475569;
            line-height: 1.65;
            text-align: left;
          }

          .certificate-template-5-body p {
            margin: 0 0 10px;
          }

          .certificate-template-5-name {
            font-weight: 700;
            color: #0f172a;
            font-size: clamp(12px, 1.25vw, 14px);
          }

          .certificate-template-5-em {
            font-weight: 600;
            color: #1e293b;
          }

          .certificate-template-5-divider {
            height: 1px;
            background: linear-gradient(90deg, transparent, #cbd5e1, transparent);
            margin: 8px 0;
          }

          .certificate-template-5-details {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 8px;
          }

          .certificate-template-5-detail {
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 8px 10px;
            background: #f8fafc;
            display: flex;
            gap: 7px;
            align-items: flex-start;
            min-width: 0;
          }

          .certificate-template-5-detail svg {
            color: #1e3a8a;
            font-size: 14px;
            margin-top: 1px;
            flex-shrink: 0;
          }

          .certificate-template-5-detail-label {
            font-size: 8px;
            text-transform: uppercase;
            letter-spacing: 0.06em;
            color: #64748b;
            font-weight: 700;
            font-family: 'Inter', sans-serif;
          }

          .certificate-template-5-detail-value {
            font-size: 10px;
            color: #0f172a;
            font-weight: 600;
            word-break: break-word;
          }

          .certificate-template-5-result {
            margin-top: 8px;
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 8px;
          }

          .certificate-template-5-meta {
            margin-top: 8px;
            display: flex;
            flex-wrap: wrap;
            gap: 10px 18px;
            font-size: 10px;
            color: #475569;
            font-family: 'Inter', sans-serif;
          }

          .certificate-template-5-meta strong {
            color: #0f172a;
            font-weight: 600;
          }

          .certificate-template-5-validity {
            margin-top: 6px;
            font-size: 9px;
            color: #64748b;
            font-family: 'Inter', sans-serif;
          }

          .certificate-template-5-side {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .certificate-template-5-photo {
            width: 100%;
            max-width: 116px;
            aspect-ratio: 3/4;
            border: 3px double #1e3a8a;
            border-radius: 6px;
            overflow: hidden;
            background: #f1f5f9;
          }

          .certificate-template-5-photo img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .certificate-template-5-photo-ph {
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #94a3b8;
            font-size: 9px;
            text-align: center;
            padding: 4px;
          }

          .certificate-template-5-qr img {
            width: 58px;
            height: 58px;
            display: block;
          }

          .certificate-template-5-qr-cap {
            font-size: 7px;
            color: #64748b;
            margin-top: 2px;
            text-align: center;
          }

          .certificate-template-5-footer {
            margin-top: auto;
            padding-top: 10px;
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 10px;
            flex-shrink: 0;
          }

          .certificate-template-5-sign {
            text-align: center;
            font-family: 'Inter', sans-serif;
          }

          .certificate-template-5-seal {
            width: 42px;
            height: 42px;
            margin: 0 auto 4px;
            border: 2px solid #64748b;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            background: #f8fafc;
            color: #64748b;
          }

          .certificate-template-5-seal svg {
            font-size: 16px;
          }

          .certificate-template-5-sign-line {
            border-top: 1px solid #0f172a;
            margin-top: 22px;
            padding-top: 5px;
            font-size: 8px;
            font-weight: 700;
            color: #0f172a;
            text-transform: uppercase;
            letter-spacing: 0.05em;
          }

          .certificate-template-5-sign-sub {
            font-size: 8px;
            color: #64748b;
            margin-top: 2px;
            font-weight: 500;
            text-transform: none;
          }

          @media (max-width: 720px) {
            .certificate-template-5-main {
              grid-template-columns: 1fr;
            }
            .certificate-template-5-side {
              flex-direction: row;
              justify-content: center;
              flex-wrap: wrap;
            }
            .certificate-template-5-details,
            .certificate-template-5-result {
              grid-template-columns: 1fr;
            }
            .certificate-template-5-footer {
              grid-template-columns: 1fr;
            }
          }

          @media print {
            @page {
              size: A4 landscape;
              margin: 10mm;
            }
            body * {
              visibility: hidden;
            }
            .certificate-template-5-page,
            .certificate-template-5-page * {
              visibility: visible;
            }
            .certificate-template-5-page {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              background: #fff;
              min-height: auto;
              padding: 0;
              margin-left: 0 !important;
            }
            .certificate-template-5-actions,
            .certificate-template-5-actions * {
              display: none !important;
              visibility: hidden !important;
            }
            .certificate-template-5-wrap {
              width: 100%;
            }
            .certificate-template-5-card {
              box-shadow: none;
              border-radius: 0;
              max-height: none;
              aspect-ratio: auto;
              min-height: 185mm;
            }
          }
        `}
      </style>

      <div className="certificate-template-5-wrap">
        <div className="certificate-template-5-actions">
          <button type="button" className="certificate-template-5-print-btn" onClick={handlePrint}>
            Print / Download
          </button>
        </div>

        <article className="certificate-template-5-card">
          <div className="certificate-template-5-watermark" aria-hidden>
            Academic Record
          </div>

          <div className="certificate-template-5-inner">
            <header className="certificate-template-5-head">
              {logoResolved ? (
                <img src={logoResolved} alt="" className="certificate-template-5-logo" />
              ) : (
                <div className="certificate-template-5-logo-ph">Logo</div>
              )}
              <p className="certificate-template-5-institute">{instName || 'Institute Name'}</p>
              <h1 className="certificate-template-5-title">{titlePrinted}</h1>
              <div className="certificate-template-5-title-line" />
            </header>

            <div className="certificate-template-5-main">
              <div className="certificate-template-5-body">
                <p>
                  This is to certify that{' '}
                  <span className="certificate-template-5-name">{studName || '—'}</span>
                  {parentsLine ? (
                    <>
                      ,<br />
                      {parentsLine},
                    </>
                  ) : admission ? (
                    ','
                  ) : null}
                  {admission ? (
                    <>
                      <br />
                      bearing Admission No.{' '}
                      <span className="certificate-template-5-em">{admission}</span>,
                    </>
                  ) : null}
                  <br />
                  has successfully passed the course/examination in{' '}
                  <span className="certificate-template-5-em">{crsName || '—'}</span>.
                </p>
                <p>
                  The student has completed the course during the period from{' '}
                  <span className="certificate-template-5-em">{start || '—'}</span> to{' '}
                  <span className="certificate-template-5-em">{end || '—'}</span>, and has met all
                  the academic requirements prescribed by the institute.
                </p>

                <div className="certificate-template-5-divider" />

                <div className="certificate-template-5-details">
                  <div className="certificate-template-5-detail">
                    <FaBookOpen aria-hidden />
                    <div>
                      <div className="certificate-template-5-detail-label">Course name</div>
                      <div className="certificate-template-5-detail-value">{crsName || '—'}</div>
                    </div>
                  </div>
                  <div className="certificate-template-5-detail">
                    <FaClock aria-hidden />
                    <div>
                      <div className="certificate-template-5-detail-label">Duration</div>
                      <div className="certificate-template-5-detail-value">{duration || '—'}</div>
                    </div>
                  </div>
                  <div className="certificate-template-5-detail">
                    <FaCalendarAlt aria-hidden />
                    <div>
                      <div className="certificate-template-5-detail-label">Course period</div>
                      <div className="certificate-template-5-detail-value">
                        {coursePeriod || '—'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="certificate-template-5-result">
                  <div className="certificate-template-5-detail">
                    <FaChartLine aria-hidden />
                    <div>
                      <div className="certificate-template-5-detail-label">Percentage</div>
                      <div className="certificate-template-5-detail-value">
                        {pct !== null ? `${pct}%` : '—'}
                      </div>
                    </div>
                  </div>
                  <div className="certificate-template-5-detail">
                    <FaMedal aria-hidden />
                    <div>
                      <div className="certificate-template-5-detail-label">Grade</div>
                      <div className="certificate-template-5-detail-value">{grade || '—'}</div>
                    </div>
                  </div>
                  <div className="certificate-template-5-detail">
                    <FaAward aria-hidden />
                    <div>
                      <div className="certificate-template-5-detail-label">Remark</div>
                      <div className="certificate-template-5-detail-value">{remark || '—'}</div>
                    </div>
                  </div>
                </div>

                <div className="certificate-template-5-meta">
                  <span>
                    <strong>Certificate No:</strong> {certNum || '—'}
                  </span>
                  <span>
                    <strong>Issue Date:</strong> {issue || '—'}
                  </span>
                  {declaration ? (
                    <span>
                      <strong>Date of Declaration:</strong> {declaration}
                    </span>
                  ) : null}
                  {resultClass ? (
                    <span>
                      <strong>Classification:</strong> {resultClass}
                    </span>
                  ) : null}
                </div>
                <div className="certificate-template-5-validity">
                  This certificate is issued as an academic record and valid only with institute
                  authorization.
                </div>
              </div>

              <aside className="certificate-template-5-side">
                <div className="certificate-template-5-photo">
                  {showPhoto ? (
                    <img src={photoSrc} alt="" onError={() => setPhotoFailed(true)} />
                  ) : !fallbackPhotoFailed ? (
                    <img
                      src={defaultPhoto}
                      alt=""
                      onError={() => setFallbackPhotoFailed(true)}
                    />
                  ) : (
                    <div className="certificate-template-5-photo-ph">Photo</div>
                  )}
                </div>
                <div className="certificate-template-5-qr">
                  <img src={qrSrc} alt="" width={58} height={58} />
                  <div className="certificate-template-5-qr-cap">Verification</div>
                </div>
              </aside>
            </div>

            <footer className="certificate-template-5-footer">
              <div className="certificate-template-5-sign">
                <div className="certificate-template-5-sign-line">Controller of Examination</div>
                <div className="certificate-template-5-sign-sub">Exam Cell</div>
              </div>
              <div className="certificate-template-5-sign">
                <div className="certificate-template-5-sign-line">{authorityName}</div>
                <div className="certificate-template-5-sign-sub">Authorized Signatory</div>
              </div>
              <div className="certificate-template-5-sign">
                <div className="certificate-template-5-sign-line">Principal / Director</div>
                <div className="certificate-template-5-sign-sub">Academic Head</div>
              </div>
              <div className="certificate-template-5-sign">
                <div className="certificate-template-5-seal">
                  <FaUniversity aria-hidden />
                </div>
                <div className="certificate-template-5-sign-line">Official Seal</div>
                <div className="certificate-template-5-sign-sub">Optional</div>
              </div>
            </footer>
          </div>
        </article>
      </div>
    </div>
  )
}

export default CertificateTemplate5
